export type HelpEntry = {
  title: string;
  body: string;
};

export const LOCATION_FIELD_HELP = {
  publicAccess: {
    title: "Public access",
    body: "Can anyone use this location?\n\nAnswer 'no' if it's a private club, school playground, gated residence, or otherwise restricted. Our stations are deployed in public spaces, so publicly accessible spots are strongly preferred. Either way, keep filling out the rest of the form as normal.",
  },
  open247: {
    title: "Open 24/7",
    body: "Is this location open 24 hours per day?\n\nIf the area is gated, has posted closing hours, or is locked at night, answer 'no' and fill in the opening hours below.",
  },
  openingHours: {
    title: "Opening hours",
    body: "Enter the opening hours for this location as best you can.\n\nExamples: 'Mon–Fri 8:00–22:00, weekends 9:00–20:00' or 'Daily 6:00–23:00'. If signage is posted on-site, transcribe it directly.",
  },
  overviewPhotos: {
    title: "Overview photos",
    body: "Take at least 3 photos that show every play area present at this location.\n\nOne spot can include multiple play zones — a ping-pong table + basketball court + grassy area all count. Don't overlook any area. Get close even if occupied; poor, distant photos won't do. Extra photos are better than too few.",
  },
  passingByVisibility: {
    title: "Passing-by visibility",
    body: "Is this location visible to pedestrians passing by?\n\nGood visibility from streets and paths makes the area feel safer and increases usage. If the location is tucked away or hidden behind buildings, answer 'no'.",
  },
  nearbyHabitations: {
    title: "Nearby habitations",
    body: "Are there apartments or houses within roughly 100 meters?\n\nNearby residents contribute to passive surveillance (more eyes on the area) and usually mean steadier foot traffic.",
  },
  signsOfDegradation: {
    title: "Signs of degradation",
    body: "Any visible damage or degradation at this location?\n\nLook for graffiti, broken equipment, litter, vandalism, or wear and tear. If yes, take photos of the degradation so we can assess severity.",
  },
  janitors: {
    title: "Janitors",
    body: "Are there janitors or caretakers for this location?\n\nActive maintenance staff indicates the area is looked after, which is a strong positive signal. You might spot them on-site, or see maintenance schedules, municipal signage, or contact info posted nearby.",
  },
  lighting: {
    title: "Lamppost / artificial lighting",
    body: "Can the location be used at night or after sunset?\n\nCheck for lampposts, floodlights, or other artificial lighting that illuminate the play areas. Nighttime usability roughly doubles the value of a station.",
  },
  dubiousPeople: {
    title: "Dubious people nearby",
    body: "Are there dubious people around, or signs of dubious activity?\n\nLook for indicators like drug paraphernalia, aggressive loitering, or visibly unsafe behavior. This is a judgment call — trust your gut about whether you'd feel safe using the station yourself.",
  },
  videoCameras: {
    title: "Video cameras",
    body: "Are there video cameras overlooking the general area?\n\nCCTV (municipal or private) is a strong deterrent against vandalism and theft. Scan lampposts, building facades, and entrance arches for cameras pointing at the play areas.",
  },
} satisfies Record<string, HelpEntry>;

export const PIL_FIELD_HELP = {
  gpsLocation: {
    title: "GPS location",
    body: "Record where this installation spot is.\n\nStand right next to the pole, then tap 'Get GPS coordinates'. Your phone's location is captured automatically. Tap it again to update if you move.\n\nFor best accuracy: allow location permission when prompted, and give your phone a few seconds to lock on outdoors (GPS can be rough in the first moment).",
  },
  polePhotos: {
    title: "Pole / mounting-point photos",
    body: "Take 3 photos of the pole you've selected:\n\n1. Wide view — pole in its environment, so we can see where it sits relative to the play areas.\n2. Side/profile — pole from the side.\n3. Base/footing — anchoring and stability at the ground.\n\nPole selection tips:\n• Entrance or edge of the area preferred (better visibility).\n• Basketball hoop: OK. Football/soccer goal: NOT OK.\n• Lamppost: preferably not — if unavoidable, make sure the station won't block any electrical trapdoor.\n• Space behind the pole is ideal (needed for the bandtool).\n• Flat surface preferred — avoid poles with outgrowths.\n• Shake the pole to check stability.",
  },
  isViable: {
    title: "Is this spot viable?",
    body: "Mark whether this specific pole/spot is a realistic mounting candidate.\n\nIf the pole feels unstable, is in the way of activity, or is otherwise problematic, mark it not viable and explain in the comments. Not-viable spots still count toward the minimum and are kept as a record — add another installation spot nearby if this one doesn't work.",
  },
  generalComments: {
    title: "General comments",
    body: "Add any remarks that will help the team evaluate this spot:\n\n• Vibe of the area (lively, quiet, abandoned).\n• Attendance / foot traffic you observed.\n• Obstacles or concerns.\n• Why you picked this pole specifically.\n• Anything unusual.\n\nEven if the spot doesn't feel ideal, note it here and still record the pole — we'd rather have too much context than too little.",
  },
} satisfies Record<string, HelpEntry>;

export const FAQ_SECTIONS: {
  title: string;
  items: { q: string; a: string }[];
}[] = [
  {
    title: "Before you start",
    items: [
      {
        q: "What am I doing out here?",
        a: "Our company deploys self-service sports stations in public spaces. Mapping happens in two phases: digital mapping (we pre-select spots on a map) and physical mapping (you, on the ground, confirm each spot and figure out how to install the stations).\n\nYour mission is to visit every provided spot, evaluate it using precise criteria, and document your analysis with text and photos.",
      },
      {
        q: "Does one map pin equal one installation spot?",
        a: "No. Map pins are locations — and one location can end up with zero, one, or several installation spots, depending on what you find on-site.\n\n• Several spots: bigger areas (multiple courts, multiple entrances) may warrant more than one.\n• Zero spots: if there's no usable pole, the area feels unsafe, or the spot isn't popular, it's fine to record that nothing fits (mark each attempt not viable and leave a comment).\n\nRule of thumb: no matter where the pin sits, walk the whole complex or park before deciding.",
      },
      {
        q: "Some installation spots are already on the map — what do I do with them?",
        a: "Those are pre-placed by your admin (you'll see a 'Pre-placed' badge on the spot). Treat them the same as spots you add yourself: visit the pole, take the 3 pole photos (wide / side / base), capture GPS, set the viable toggle, add comments, and mark the spot done.\n\nIf a pre-placed spot turns out to be unusable, mark it not viable and explain why — don't delete it.",
      },
      {
        q: "What do I need before heading out?",
        a: "• A phone with GPS and camera permissions enabled for the browser.\n• The mapping share link from your admin.\n• The location list on the home screen — it shows every location you're responsible for, with distance from your current position.",
      },
    ],
  },
  {
    title: "At the location",
    items: [
      {
        q: "How do I evaluate a location?",
        a: "The form walks you through the real criteria — the fields below are exactly what matters:\n\n• Public access (can anyone use it?).\n• Opening hours / 24/7.\n• Passing-by visibility (1–5).\n• Nearby habitations (1–5).\n• Signs of degradation.\n• Janitors / caretakers.\n• Lamppost or artificial lighting.\n• Dubious people nearby.\n• Video cameras.\n\nReview the entire area — one location can include multiple play zones (ping-pong + basketball + grass field, etc.). Consider free-play spaces too (a grass field is a potential play area).",
      },
      {
        q: "How many overview photos should I take?",
        a: "At least 3 photos that together cover every play area present. Don't miss any. Get close even if an area is occupied — distant or poor-quality photos won't cut it. Extra photos are always better than too few.",
      },
      {
        q: "How many installation spots should I find?",
        a: "Your admin sets a minimum per location. When you're short, the page shows a yellow banner at the top of the installation-spots list: 'Find N more installation spots'. The 'X of Y done' counter tells you progress.\n\nYou can always add more than the minimum if the area warrants it. Base it on available space, foot traffic, safety (not in anyone's way), and your judgment. If you can't find enough usable spots, record the ones you tried — mark any that don't work as not viable with a comment explaining why.",
      },
    ],
  },
  {
    title: "Installation spots (poles)",
    items: [
      {
        q: "What makes a good pole?",
        a: "Preferred:\n• Entrance or edge of the area (better visibility).\n• Basketball hoop: OK.\n• Space behind the pole (needed for the bandtool).\n• Flat surface — no outgrowths.\n\nAvoid:\n• Football/soccer goals.\n• Lampposts (only if unavoidable — and never block an electrical trapdoor).\n\nAlways shake the pole to check stability before committing to it.",
      },
      {
        q: "How do I capture the pole's GPS?",
        a: "Stand right next to the pole, then tap 'Get GPS coordinates'. Your phone's location is captured automatically. Tap it again to update if you move.\n\nFor best accuracy: allow location permission when prompted, and give your phone a few seconds to lock on outdoors (GPS can be rough in the first moment).",
      },
      {
        q: "What 3 photos do I need per pole?",
        a: "1. Wide view — pole in its environment.\n2. Side/profile — pole from the side.\n3. Base/footing — anchoring and stability at the ground.\n\nMake the pole's position relative to the play areas readable from the wide-view photo.",
      },
      {
        q: "Why does it say 'Find N more installation spots'?",
        a: "Your admin sets a minimum number of installation spots per location. The yellow banner shows up when you're still short of that minimum.\n\nOnce you place enough spots, the banner clears. You can always add more than the minimum if the area warrants it — the minimum is a floor, not a cap.",
      },
    ],
  },
  {
    title: "Final checklist & good practices",
    items: [
      {
        q: "Before I submit, what should I double-check?",
        a: "Per location:\n☐ All 10 location fields filled (public access through video cameras).\n☐ At least 3 overview photos.\n☐ Minimum installation spots placed (no yellow 'Find N more' banner).\n\nPer installation spot:\n☐ GPS captured.\n☐ All 3 pole photos (wide, side, base).\n☐ Viable toggle set (yes or no).\n☐ Comments added if anything is unusual.\n☐ Spot marked done.",
      },
      {
        q: "Why isn't an installation spot turning green / marked done?",
        a: "To mark a spot done it needs: GPS captured, all 3 pole photos (wide, side, base), and the viable toggle set. If any of these are missing, the 'Mark as done' button stays disabled and the spot stays yellow (not started) or blue (in progress).\n\nOpen the spot, check which requirement is missing, fill it in, then tap 'Mark as done'.",
      },
      {
        q: "Any common mistakes to avoid?",
        a: "• Don't overlook any area — a single location can include multiple play zones.\n• Get close even when areas are occupied; don't settle for distant photos.\n• Be precise and rigorous — your observations directly help us pitch the project to the city.\n• Take extra photos if you're unsure. Better too many than too few.\n\nIf work is poorly executed we reserve the right to ask for a revisit, so it's worth getting it right the first time.",
      },
    ],
  },
];
